import { useState, useRef } from 'react';
import { useDownloadURL } from 'react-firebase-hooks/storage';
import { ref as storageRef } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import { useEffect } from 'react';
import { FaRotateRight } from 'react-icons/fa6';
import Button from 'react-bootstrap/Button';

const ImageFromStorage = ({
  link,
  height,
  width,
  fullWidth,
  objectFit,
  style,
  editable,
  isAdmin,
  ...rest
}) => {
  const [heightForFullWidth, setHeightForFullWidth] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [rotation, setRotation] = useState(1); // 1 = 0°, 3 = 180°, 6 = 90°, 8 = 270°
  const [processingError, setProcessingError] = useState(false);

  const [downloadUrl] = useDownloadURL(storageRef(storage, link));
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  function adjustImageOrientation(img, rotation, callback) {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('Canvas context not available');
        callback(null);
        return;
      }

      // Set canvas dimensions based on rotation
      if ([6, 8].includes(rotation)) {
        canvas.width = img.height;
        canvas.height = img.width;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }

      // Apply transformations and draw image
      ctx.save(); // Save the current state

      switch (rotation) {
        case 3: // 180°
          ctx.transform(-1, 0, 0, -1, img.width, img.height);
          break;
        case 6: // 90°
          ctx.translate(img.height, 0);
          ctx.transform(0, 1, -1, 0, 0, 0);
          break;
        case 8: // 270°
          ctx.translate(0, img.width);
          ctx.transform(0, -1, 1, 0, 0, 0);
          break;
        default: // 0°
          ctx.transform(1, 0, 0, 1, 0, 0);
      }

      let imageDrawn = false;
      try {
        // Try to draw the image
        ctx.drawImage(img, 0, 0, img.width, img.height);

        // Immediately check if it actually drew something
        const testPixel = ctx.getImageData(
          canvas.width / 2,
          canvas.height / 2,
          1,
          1
        );
        imageDrawn = testPixel.data[3] > 0; // Check if alpha > 0

        if (!imageDrawn) {
          // Alternative: Try drawing to a smaller area
          const smallerWidth = Math.min(img.width, canvas.width);
          const smallerHeight = Math.min(img.height, canvas.height);
          ctx.drawImage(
            img,
            0,
            0,
            smallerWidth,
            smallerHeight,
            0,
            0,
            smallerWidth,
            smallerHeight
          );

          // Check again
          const retestPixel = ctx.getImageData(
            canvas.width / 4,
            canvas.height / 4,
            1,
            1
          );
          imageDrawn = retestPixel.data[3] > 0;
        }
      } catch (drawError) {
        console.error('Canvas drawImage error:', drawError);
        imageDrawn = false;
      }

      ctx.restore(); // Restore the saved state

      // Convert to data URL with quality control
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

      // Additional debugging - test if canvas context works at all
      const testCanvas = document.createElement('canvas');
      testCanvas.width = 100;
      testCanvas.height = 100;
      const testCtx = testCanvas.getContext('2d');
      testCtx.fillStyle = 'red';
      testCtx.fillRect(0, 0, 50, 50);

      // Validate the generated data URL (more strict validation)
      if (dataUrl && dataUrl.length > 1000) {
        callback(dataUrl);
      } else {
        console.error(
          'Generated data URL is invalid or too small, length:',
          dataUrl?.length
        );
        callback(null);
      }
    } catch (error) {
      console.error('Error in adjustImageOrientation:', error);
      callback(null, { error: `Canvas error: ${error.message}` });
    }
  }

  useEffect(() => {
    if (downloadUrl && imgRef?.current && isAdmin && !processingError) {
      setImageSrc(null); // Reset while processing

      const img = new Image();

      img.onload = () => {
        // Validate image dimensions
        if (img.width === 0 || img.height === 0) {
          console.error('Image has invalid dimensions');
          setProcessingError(true);
          return;
        }

        try {
          adjustImageOrientation(img, rotation, (dataUrl) => {
            if (dataUrl) {
              setImageSrc(dataUrl);
            } else {
              console.error(
                'Failed to process image orientation, falling back to original'
              );
              setProcessingError(true);
            }
          });
        } catch (error) {
          console.error('Image processing error:', error);
          setProcessingError(true);
        }
      };

      img.onerror = (error) => {
        console.error('Failed to load image for processing:', error);
        setProcessingError(true);
      };

      // Set crossOrigin before src to avoid CORS issues
      img.crossOrigin = 'anonymous';
      img.src = downloadUrl;
    }
  }, [downloadUrl, rotation, isAdmin, processingError]);

  useEffect(() => {
    if (fullWidth && imgRef?.current?.height) {
      setHeightForFullWidth(imgRef?.current?.height);
    }
  }, [fullWidth, imgRef?.current?.height]);

  const handleChangeOrientation = () => {
    setRotation((prev) => {
      const rotationStates = [1, 6, 3, 8]; // 0°, 90°, 180°, 270°
      const currentIndex = rotationStates.indexOf(prev);
      return rotationStates[(currentIndex + 1) % 4];
    });
  };

  // Determine which image source to use
  const finalImageSrc =
    isAdmin && !processingError && imageSrc ? imageSrc : downloadUrl;

  return (
    <div style={{ position: 'relative' }}>
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
      <img
        ref={imgRef}
        src={finalImageSrc}
        style={{
          objectFit: objectFit || 'contain',
          height: fullWidth ? heightForFullWidth : height,
          width: fullWidth ? '100%' : width,
          ...style,
        }}
        {...rest}
      />
      {editable && (
        <Button
          variant="danger"
          onClick={handleChangeOrientation}
          style={{
            position: 'absolute',
            top: 64,
            right: 64,
            cursor: 'pointer',
          }}
        >
          <FaRotateRight size={24} />
        </Button>
      )}
    </div>
  );
};

export default ImageFromStorage;
